import CircleCreateIcon from '@assets/jsx-icon/CircleCreateIcon';
import ICDelete from '@assets/jsx-icon/ICDelete';
import BaseCheckbox from '@components/BaseCheckbox';
import BaseTable from '@components/BaseTable';
import PopUpBase from '@components/PopUpBase';
import PopUpWarning from '@components/PopUpWarning';
import { CellOfRow } from '@components/tables';
import { pushSuccess } from '@components/toast';
import { permitExemptHttpController, vehicleExemptHttpController } from '@controllers/index';
import { VehicleExempt } from '@EcamModel/model/VehicleExempt';
import { usePagedSelection } from '@hooks/usePagedSelection';
import usePopUp from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, FormGroup, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import { DangerTooltip } from '@pages/cameras/list/components/TooltipDangerIconButton';
import VRNInputs from '@pages/exemptions/create/components/VRNInputs';
import { ellipsisStyles } from '@pages/task-management';
import color from '@theme/Colors';
import { FC, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { getScopeDisplayContent } from '../helpers';
import { mappingScopePermit, PropsListData } from '../utils';

export default function ListDataPermitsVehicle(props: PropsListData) {
    const { listExemptPermits, isLoading, popUpConfirmDelete, onSetItemVRNsPlate, onChangePage, searchValue } = props;
    const queryClient = useQueryClient();

    const selection = usePagedSelection<VehicleExempt>({
        currentPageItems: listExemptPermits?.rows || [],
        getId: (item: VehicleExempt) => item.Id!,
    });

    const deleteVehicleExemptPopup = usePopUp();
    const bulkDeletePermitExemptSelectedPopup = usePopUp();
    const createVehicleExemptPopup = usePopUp();

    const [deletingItem, setDeletingItem] = useState<{ vehicleId: number } | null>(null);
    const [selectedIdsForDelete, setSelectedIdsForDelete] = useState<number[]>([]);
    const [currentPermitItem, setCurrentPermitItem] = useState<VehicleExempt | null>(null);
    const [newPlates, setNewPlates] = useState<string[]>(['']);
    const [highlightPlate, setHighlightPlate] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (searchValue && searchValue.trim()) {
            setHighlightPlate(searchValue.trim().toLowerCase());
        } else {
            setHighlightPlate('');
        }
    }, [searchValue]);

    const handleBulkDeletePermitExemptSelected = () => {
        const selectedIds = selection.getSelectedIds();
        setSelectedIdsForDelete(selectedIds);
        bulkDeletePermitExemptSelectedPopup.setTrue();
    };

    const handleDeleteVehicleExempt = (vehicleId: number) => {
        setDeletingItem({ vehicleId });
        deleteVehicleExemptPopup.setTrue();
    };

    const confirmDeleteVehicleExempt = async () => {
        if (!deletingItem) return;

        try {
            await vehicleExemptHttpController.delete(deletingItem.vehicleId.toString());
            queryClient.invalidateQueries(['listExemptPermits']);
            deleteVehicleExemptPopup.onClose();
            setDeletingItem(null);
        } catch (error) {
            console.error('Error deleting vehicle exempt:', error);
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedIdsForDelete.length === 0) return;

        try {
            await vehicleExemptHttpController.deleteMultipleVehicleExempt(selectedIdsForDelete);
            queryClient.invalidateQueries(['listExemptPermits']);
            bulkDeletePermitExemptSelectedPopup.onClose();
            setSelectedIdsForDelete([]);
            selection.clearSelection();
        } catch (error) {
            console.error('Error deleting multiple permit exempts:', error);
        }
    };

    const handleCreateVehicle = (item: VehicleExempt) => {
        setCurrentPermitItem(item);
        setNewPlates(['']);
        createVehicleExemptPopup.setTrue();
    };

    const confirmCreateVehicle = async () => {
        if (!currentPermitItem) return;
        setIsCreating(true);

        const validPlates = newPlates.filter((plate) => plate.trim());

        if (validPlates.length === 0) return;

        try {
            for (const plate of validPlates) {
                await vehicleExemptHttpController.upsert({
                    PermitExemptId: currentPermitItem.PermitExemptId!,
                    Plate: plate.trim(),
                });
            }

            queryClient.invalidateQueries(['listExemptPermits']);
            pushSuccess('Vehicles added successfully');
            createVehicleExemptPopup.onClose();
            setCurrentPermitItem(null);
            setNewPlates(['']);
        } catch (error) {
            console.error('Error creating vehicle exempts:', error);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Box>
            <Stack direction={'row'} justifyContent={'space-between'} width={'100%'} alignItems={'center'} mb={2}>
                <Stack direction={'row'} alignItems={'center'}>
                    <FormGroup
                        sx={{
                            paddingLeft: '10px',
                            display: 'flex',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <BaseCheckbox
                                    name="select-all"
                                    checked={selection.isAllCurrentPageSelected}
                                    onChange={selection.toggleAllCurrentPage}
                                />
                            }
                            label={
                                <Typography ml={1} mt={'1px'}>
                                    Select all
                                </Typography>
                            }
                        />
                    </FormGroup>
                    <Button
                        size="small"
                        variant="errorOutlined"
                        sx={{ px: 1, py: 0 }}
                        onClick={handleBulkDeletePermitExemptSelected}
                        disabled={selection.selectedCount === 0}
                    >
                        Delete ({selection.selectedCount})
                    </Button>
                </Stack>
                <Typography textAlign={'right'} sx={{ color: color.priText }}>
                    Total: {listExemptPermits?.total}
                </Typography>
            </Stack>
            <BaseTable
                isLoading={isLoading}
                paging={listExemptPermits}
                tableHeaders={[
                    '',
                    'Plate',
                    'Scope',
                    'Location',
                    'Zone',
                    'Camera',
                    'Geo-zone',
                    'Reason',
                    'Note',
                    '',
                ]}
                setPage={onChangePage}
                expandable={false}
                renderedItem={(item: VehicleExempt) => {
                    const permitInfo = item.PermitExempt;
                    const scopeContent = permitInfo
                        ? getScopeDisplayContent(permitInfo)
                        : { location: '', zone: '', camera: '', geoZone: '' };
                    const cameraId = permitInfo?.Camera?.Id || permitInfo?.GeoZone?.Camera?.Id;

                    return (
                        <>
                            <CellOfRow
                                value={
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selection.toggleOne(item.Id!);
                                        }}
                                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <BaseCheckbox checked={selection.isSelected(item.Id!)} onChange={() => {}} />
                                    </Box>
                                }
                                align="center"
                                sx={{ width: '20px', padding: '0px' }}
                            />

                            <CellOfRow value={item?.Plate || ''} align="left" />

                            <CellOfRow
                                value={permitInfo ? mappingScopePermit[permitInfo?.ScopeType] : ''}
                                align="left"
                            />

                            <CellOfRow value={scopeContent.location} align="left" />
                            <CellOfRow value={scopeContent.zone} align="left" />
                            <CellOfRow
                                value={
                                    scopeContent.camera ? (
                                        <>
                                            {cameraId ? (
                                                <Link
                                                    to={`/cameras/${cameraId}`}
                                                    style={{ textDecoration: 'none' }}
                                                    target="_blank"
                                                >
                                                    <Typography
                                                        sx={{
                                                            ...ellipsisStyles,
                                                            color: color.secondary,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {scopeContent.camera}
                                                    </Typography>
                                                </Link>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        ...ellipsisStyles,
                                                        color: color.textPrimary,
                                                    }}
                                                >
                                                    {scopeContent.camera}
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        ''
                                    )
                                }
                                align="left"
                            />
                            <CellOfRow value={scopeContent.geoZone} align="left" />

                            <CellOfRow value={permitInfo?.ContraventionReasonTranslation?.Summary ?? ''} align="left" />

                            <TableCell align="left">
                                <Typography>{permitInfo?.Notes?.trim() ?? ''}</Typography>
                            </TableCell>

                            <CellOfRow
                                value={
                                    <Stack direction={'row'} spacing={0.7} alignItems={'center'}>
                                        {/* <Tooltip placement="top" title="Add vehicle">
                                            <Box
                                                sx={{
                                                    color: '#85858A',
                                                    transition: '.2s ease-in-out',
                                                    cursor: 'pointer',
                                                    height: '20px',
                                                    '&:hover': {
                                                        color: (theme) => theme.palette.secondary.main,
                                                    },
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCreateVehicle(item);
                                                }}
                                            >
                                                <CircleCreateIcon width={16} height={16} />
                                            </Box>
                                        </Tooltip> */}
                                        <DangerTooltip placement="top" title="Delete">
                                            <Box
                                                sx={{
                                                    cursor: 'pointer',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteVehicleExempt(item.Id!);
                                                }}
                                            >
                                                <ICDelete fontSize={18} />
                                            </Box>
                                        </DangerTooltip>
                                    </Stack>
                                }
                                align="left"
                            />
                        </>
                    );
                }}
            />

            <PopUpWarning
                open={deleteVehicleExemptPopup.open}
                onClose={deleteVehicleExemptPopup.onClose}
                onConfirm={confirmDeleteVehicleExempt}
                title="Confirm Delete"
                message="Are you sure you want to delete this vehicle exempt?"
            />

            <PopUpWarning
                open={bulkDeletePermitExemptSelectedPopup.open}
                onClose={bulkDeletePermitExemptSelectedPopup.onClose}
                onConfirm={confirmBulkDelete}
                title="Confirm Bulk Delete"
                message={`Are you sure you want to delete ${selectedIdsForDelete.length} selected vehicle exempt(s)?`}
            />

            <PopUpBase
                {...createVehicleExemptPopup}
                dialogProps={{
                    fullWidth: true,
                    sx: {
                        '& .MuiDialog-paper': {
                            borderRadius: '10px',
                            minWidth: 630,
                        },
                        zIndex: (theme) => theme.zIndex.modal,
                    },
                }}
                titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
                subTitleProps={{ sx: { color: 'gray' } }}
                minWidthButton={120}
                title="Add New Vehicle"
                desc={<VRNInputs vrns={newPlates} onChange={(vrns) => setNewPlates(vrns)} />}
                customActions={
                    <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Button variant="cancel" onClick={createVehicleExemptPopup.onClose} sx={{ minWidth: 120 }}>
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            onClick={confirmCreateVehicle}
                            disabled={newPlates.some((plate) => plate.trim() === '')}
                            sx={{ minWidth: 120 }}
                            loading={isCreating}
                            loadingPosition="center"
                        >
                            Save
                        </LoadingButton>
                    </Stack>
                }
            />
        </Box>
    );
}

type FormattedPlatesProps = {
    plates: VehicleExempt[];
};

export const FormattedPlates: FC<FormattedPlatesProps> = ({ plates }) => {
    return (
        <Box>
            {plates.map((plate, index) => (
                <Typography key={index} variant="body1" noWrap>
                    {plate.Plate}
                </Typography>
            ))}
        </Box>
    );
};
